import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

/** One choice in a dropdown cell: what is stored, what is shown, and a hint on the right (a price, a unit). */
export interface PickOption {
  value: string;
  label: string;
  hint?: string;
  /** extra words that should find it (a code, the Spanish name) */
  search?: string;
}

type Edit<Row> =
  | { kind: 'text' | 'number'; get: (row: Row) => string; set: (row: Row, value: string) => Row | null }
  // options per row when they depend on it (a product's own units, decision 166)
  | { kind: 'pick'; options: PickOption[] | ((row: Row) => PickOption[]); get: (row: Row) => string; set: (row: Row, value: string) => Row | null; placeholder?: string };

export interface SheetColumn<Row> {
  key: string;
  header: ReactNode;
  /** a short explanation shown when the pointer rests on the header */
  hint?: string;
  /** the column's name in words, for a screen reader, when the header is not plain text */
  label?: string;
  /** a CSS width: '80px', '12ch' */
  width?: string;
  align?: 'left' | 'right' | 'center';
  render: (row: Row, index: number) => ReactNode;
  edit?: Edit<Row> | undefined;
  /** a cell of an editable column that is locked on this row (an order already invoiced) */
  canEdit?: (row: Row) => boolean;
  cellClass?: (row: Row) => string | undefined;
}

const PICK_LIMIT = 60;
const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Options whose label or search words hold every word typed, the ones that start with it first. */
export function filterOptions(options: PickOption[], query: string): PickOption[] {
  const words = fold(query).split(/\s+/).filter(Boolean);
  if (words.length === 0) return options.slice(0, PICK_LIMIT);
  const hits = options.filter((o) => {
    const hay = fold(`${o.label} ${o.search ?? ''}`);
    return words.every((w) => hay.includes(w));
  });
  const first = fold(query.trim());
  hits.sort((a, b) => Number(fold(b.label).startsWith(first)) - Number(fold(a.label).startsWith(first)));
  return hits.slice(0, PICK_LIMIT);
}

/**
 * A spreadsheet the office can drive with the keyboard, like Excel: click a cell to select it; type, press
 * Enter or double-click to edit; Enter saves and goes down, Tab saves and goes right, Escape cancels; arrows
 * move; Delete clears. A dropdown cell filters its list as you type and Enter takes the highlighted choice.
 */
export function Sheet<Row>({
  columns,
  rows,
  rowKey,
  onRowsChange,
  label,
  footer,
  rowClass,
  minWidth,
  onOpen,
  stickyFirst,
  rowLabel,
}: {
  columns: SheetColumn<Row>[];
  rows: Row[];
  rowKey: (row: Row, index: number) => string;
  /** a read-only grid can leave this out */
  onRowsChange?: (rows: Row[], changed: { row: number; col: string }) => void;
  /** Enter or a double-click on a cell that cannot be typed in opens the row (a list of records) */
  onOpen?: (row: Row) => void;
  label: string;
  footer?: ReactNode;
  rowClass?: (row: Row) => string | undefined;
  /** below this the grid scrolls sideways instead of squeezing columns to nothing: '1100px' */
  minWidth?: string;
  /** the first column stays in place while the grid scrolls sideways (the item names) */
  stickyFirst?: boolean;
  /** the row's name in words, so the editor is announced "M2 Yellow Onions" */
  rowLabel?: (row: Row) => string;
}) {
  // the selected cell follows its row (by key) when rows are added, hidden or re-sorted; the index is the fallback
  const [active, setActiveRaw] = useState<{ r: number; c: number; key: string | null }>({ r: 0, c: firstEditable(columns), key: null });
  const setActive = (at: { r: number; c: number }) => setActiveRaw({ ...at, key: rows[at.r] !== undefined ? rowKey(rows[at.r]!, at.r) : null });
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [hi, setHi] = useState(0);
  const [anchor, setAnchor] = useState<{ top?: number; bottom?: number; left: number; width: number } | null>(null);
  const gridRef = useRef<HTMLTableElement>(null);
  const uid = useId();
  const cellId = (ri: number, ci: number) => `${uid}-c${ri}-${ci}`;
  const optId = (oi: number) => `${uid}-o${oi}`;
  /** set once an edit is saved or cancelled, so the editor's blur (fired as it unmounts) does not save it twice */
  const settled = useRef(true);
  const inputRef = useRef<HTMLInputElement>(null);

  const keyed = active.key === null ? -1 : rows.findIndex((row, i) => rowKey(row, i) === active.key);
  const r = keyed >= 0 ? keyed : Math.min(active.r, Math.max(0, rows.length - 1));
  const c = Math.min(active.c, columns.length - 1);
  const col = columns[c];
  const locked = !!col?.canEdit && rows[r] !== undefined && !col.canEdit(rows[r]!);
  const edit = locked ? undefined : col?.edit;
  const activeRow = rows[r];
  const matches = useMemo(
    () => (editing && edit?.kind === 'pick' ? filterOptions(typeof edit.options === 'function' ? (activeRow === undefined ? [] : edit.options(activeRow)) : edit.options, draft) : []),
    [editing, edit, draft, activeRow],
  );

  useEffect(() => {
    if (!editing) return;
    inputRef.current?.focus();
    const place = () => {
      const td = inputRef.current?.closest('td');
      if (!td || typeof td.getBoundingClientRect !== 'function') return;
      const b = td.getBoundingClientRect();
      const roomBelow = window.innerHeight - b.bottom;
      // near the bottom of the window the list opens upward
      setAnchor(roomBelow < 240 && b.top > roomBelow ? { bottom: window.innerHeight - b.top, left: b.left, width: b.width } : { top: b.bottom, left: b.left, width: b.width });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [editing, r, c]);

  const skipBlur = useRef(false);
  /** Back to the grid at once, so the next key lands on the grid; the editor's blur must not save twice. */
  const focusGrid = () => {
    skipBlur.current = true;
    // never scrolls the page: a tall sheet focused on a mouse press would scroll its top into view before the
    // mouse came up, and the click (a link to open the row) would land on another row or nothing (JL, 2026-10-25)
    gridRef.current?.focus({ preventScroll: true });
    skipBlur.current = false;
  };
  const move = (dr: number, dc: number) => {
    let nr = r + dr;
    let nc = c + dc;
    if (nc >= columns.length) {
      nc = 0;
      nr += 1;
    } else if (nc < 0) {
      nc = columns.length - 1;
      nr -= 1;
    }
    // one past the last line is allowed: a page that keeps a blank line at the end adds it in the same update
    setActive({ r: Math.max(0, Math.min(rows.length, nr)), c: Math.max(0, Math.min(columns.length - 1, nc)) });
  };
  /** Tab and the step after a pick go to the next cell that can be typed in, wrapping to the next line. */
  const moveEditable = (dir: 1 | -1, current?: Row) => {
    // the cells that can be typed in on a row: a cell locked on it (the Unit of a product sold only by the box) is skipped
    const editableOn = (row: Row | undefined) => columns.map((x, i) => (x.edit && (!x.canEdit || row === undefined || x.canEdit(row)) ? i : -1)).filter((i) => i >= 0);
    // the line as just changed (a product picked a moment ago opens its quantity): never the line as it was before
    const editable = editableOn(current ?? rows[r]);
    if (columns.every((x) => !x.edit)) return move(0, dir);
    const after = dir === 1 ? editable.find((i) => i > c) : [...editable].reverse().find((i) => i < c);
    if (after !== undefined) return setActive({ r, c: after });
    const nr = Math.max(0, Math.min(rows.length, r + dir));
    const next = editableOn(rows[nr]);
    if (next.length === 0) return setActive({ r: nr, c });
    setActive({ r: nr, c: dir === 1 ? next[0]! : next[next.length - 1]! });
  };
  const begin = (initial?: string) => {
    if (!edit || rows.length === 0) return;
    setDraft(initial ?? edit.get(rows[r]!));
    setHi(0);
    settled.current = false;
    setEditing(true);
  };
  /** Saves the cell; the line as it now reads, or null when nothing changed */
  const commit = (value: string): Row | null => {
    if (!edit || settled.current) return null;
    settled.current = true;
    const next = edit.set(rows[r]!, value);
    setEditing(false);
    if (next === null) return null;
    const copy = rows.slice();
    copy[r] = next;
    onRowsChange?.(copy, { row: r, col: col!.key });
    return next;
  };
  const cancel = () => {
    settled.current = true;
    setEditing(false);
    focusGrid();
  };

  function onGridKey(e: KeyboardEvent<HTMLDivElement>) {
    if (editing) {
      // a fast typist's next keys can land here before the editor has focus: keep them
      if (e.target === gridRef.current && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        setDraft((d) => d + e.key);
        setHi(0);
        e.preventDefault();
      }
      // and the Enter or Tab that ends what they typed (it left the product behind and moved on, the end-user test)
      else if (e.target === gridRef.current && ['Enter', 'Tab', 'Escape', 'ArrowDown', 'ArrowUp'].includes(e.key)) onInputKey(e as unknown as KeyboardEvent<HTMLInputElement>);
      return;
    }
    if (e.key === 'ArrowDown') move(1, 0);
    else if (e.key === 'ArrowUp') move(-1, 0);
    else if (e.key === 'ArrowRight') move(0, 1);
    else if (e.key === 'ArrowLeft') move(0, -1);
    else if (e.key === 'Tab') {
      // at the last cell (or the first, going back) Tab leaves the grid like any other field; a read-only grid
      // (a list or a report) does not keep Tab at all
      const editable = columns.map((x, i) => (x.edit ? i : -1)).filter((i) => i >= 0);
      if (editable.length === 0) return;
      const atEnd = e.shiftKey ? r === 0 && c <= (editable[0] ?? 0) : r >= rows.length - 1 && c >= (editable[editable.length - 1] ?? columns.length - 1);
      if (atEnd) return;
      moveEditable(e.shiftKey ? -1 : 1);
    }
    else if ((e.key === 'Enter' || e.key === 'F2') && !edit && onOpen && rows[r]) onOpen(rows[r]!);
    else if (e.key === 'Enter' || e.key === 'F2') begin();
    else if ((e.key === 'Delete' || e.key === 'Backspace') && edit) {
      settled.current = false;
      commit('');
    }
    else if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey && edit) begin(e.key);
    else return;
    e.preventDefault();
  }

  function onInputKey(e: KeyboardEvent<HTMLInputElement>) {
    const pick = edit?.kind === 'pick';
    if (pick && e.key === 'ArrowDown') setHi((h) => Math.min(matches.length - 1, h + 1));
    else if (pick && e.key === 'ArrowUp') setHi((h) => Math.max(0, h - 1));
    else if (e.key === 'Escape') cancel();
    else if (e.key === 'Enter' || e.key === 'Tab') {
      if (pick && draft.trim() === '') {
        // nothing typed in a dropdown: leave the cell as it was (Enter walking down a column must not pick the
        // first product of the list); a product is cleared with Delete on its line
        settled.current = true;
        setEditing(false);
        if (e.key === 'Enter') move(1, 0);
        else moveEditable(e.shiftKey ? -1 : 1);
        focusGrid();
        return e.preventDefault();
      }
      const value = pick ? (matches[hi]?.value ?? null) : draft;
      if (value === null) return e.preventDefault(); // nothing matches: stay in the cell
      const now = commit(value) ?? undefined;
      // Enter goes down the column (the next line), the way the office's pages work; after picking a product it goes
      // right to the quantity, and so does Tab (the end-user test: Tab skipped the quantity and a typed 3 was lost)
      if (e.key === 'Enter' && pick) moveEditable(1, now);
      else if (e.key === 'Enter') move(1, 0);
      else moveEditable(e.shiftKey ? -1 : 1, now);
      focusGrid();
    } else return;
    e.preventDefault();
  }

  return (
    <div className="sheet-wrap">
      <div className={stickyFirst ? 'sheet sticky-first' : 'sheet'}>
        <table
          ref={gridRef}
          role="grid"
          aria-label={label}
          tabIndex={0}
          onKeyDown={onGridKey}
          aria-activedescendant={rows.length && !editing ? cellId(r, c) : undefined}
          style={minWidth ? { minWidth } : undefined}
        >
          <thead>
            <tr role="row">
              {columns.map((h) => (
                <th key={h.key} role="columnheader" title={h.hint} style={h.width ? { width: h.width } : undefined} className={h.align ? `al-${h.align}` : undefined}>
                  {h.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={rowKey(row, ri)} role="row" className={rowClass?.(row)}>
                {columns.map((h, ci) => {
                  const isActive = ri === r && ci === c;
                  const canType = !!h.edit && (!h.canEdit || h.canEdit(row));
                  const classes = ['cell', h.align ? `al-${h.align}` : '', canType ? 'editable' : 'readonly', isActive ? 'active' : '', h.cellClass?.(row) ?? ''].filter(Boolean).join(' ');
                  return (
                    <td
                      key={h.key}
                      id={cellId(ri, ci)}
                      role="gridcell"
                      aria-selected={isActive}
                      aria-readonly={!canType || undefined}
                      data-col={h.key}
                      className={classes}
                      onMouseDown={(e) => {
                        if (editing && isActive) return;
                        // clicking away saves what was typed, like Excel (a half-typed product search is dropped)
                        if (editing) {
                          if (edit?.kind !== 'pick') commit(draft);
                          else setEditing(false);
                        }
                        setActive({ r: ri, c: ci });
                        if (!(e.target instanceof HTMLButtonElement)) focusGrid();
                      }}
                      onDoubleClick={() => (canType ? begin() : onOpen?.(row))}
                    >
                      {isActive && editing && h.edit && canType ? (
                        <div className="cell-editor">
                          <input
                            ref={inputRef}
                            autoFocus
                            {...(h.edit.kind === 'pick'
                              ? { role: 'combobox', 'aria-expanded': matches.length > 0, 'aria-controls': `${uid}-list`, 'aria-activedescendant': matches[hi] ? optId(hi) : undefined, 'aria-autocomplete': 'list' as const }
                              : {})}
                            aria-label={`${h.label ?? (typeof h.header === 'string' ? h.header : h.key)} ${rowLabel ? rowLabel(row) : ri + 1}`}
                            inputMode={h.edit.kind === 'number' ? 'decimal' : undefined}
                            value={draft}
                            placeholder={h.edit.kind === 'pick' ? h.edit.placeholder : undefined}
                            onChange={(e) => {
                              setDraft(e.target.value);
                              setHi(0);
                            }}
                            onKeyDown={onInputKey}
                            onBlur={() => {
                              if (skipBlur.current) return;
                              if (h.edit?.kind !== 'pick') commit(draft);
                              else setEditing(false);
                            }}
                          />
                          {h.edit.kind === 'pick' && matches.length > 0 ? (
                            <ul id={`${uid}-list`} className="pick-list" role="listbox" aria-label={typeof h.header === 'string' ? h.header : h.key} style={anchor ? { top: anchor.top, bottom: anchor.bottom, left: anchor.left, minWidth: Math.max(360, anchor.width) } : undefined}>
                              {matches.map((o, oi) => (
                                <li
                                  key={o.value}
                                  id={optId(oi)}
                                  role="option"
                                  aria-selected={oi === hi}
                                  className={oi === hi ? 'hi' : undefined}
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    moveEditable(1, commit(o.value) ?? undefined);
                                    focusGrid();
                                  }}
                                >
                                  <span>{o.label}</span>
                                  {o.hint ? <span className="pick-hint">{o.hint}</span> : null}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      ) : (
                        h.render(row, ri)
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {footer ? <tfoot>{footer}</tfoot> : null}
        </table>
      </div>
    </div>
  );
}

function firstEditable<Row>(columns: SheetColumn<Row>[]): number {
  const i = columns.findIndex((c) => c.edit);
  return i < 0 ? 0 : i;
}
